import cn from 'clsx';
import { space } from '@ubnt/ui-components/styles/designToken';
import classes from './Flex.module.scss';

const gaps = {
  none: space.alias.none, // 0
  xxs: space.alias.xxsmall, // 2px
  xs: space.alias.xsmall, // 4px
  sCompact: '6px', // TODO this is custom and should be removed since it's not a part of design system
  s: space.alias.small, // 8px
  m: space.alias['compact-medium'], // 12px
  l: space.alias.medium, // 16px
  xl: space.alias['large-1'], // 24px
  xxl: space.alias['large-2'], // 32px
};

export interface FlexOwnProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  column?: boolean;
  columnReverse?: boolean;
  rowReverse?: boolean;
  justifyStart?: boolean;
  justifyCenter?: boolean;
  justifyEnd?: boolean;
  justifyBetween?: boolean;
  justifyEvenly?: boolean;
  contentCenter?: boolean;
  contentStart?: boolean;
  contentEnd?: boolean;
  contentBaseline?: boolean;
  contentStretch?: boolean;
  contentAround?: boolean;
  contentBetween?: boolean;
  alignStart?: boolean;
  alignCenter?: boolean;
  alignEnd?: boolean;
  alignBaseline?: boolean;
  grow?: boolean;
  noShrink?: boolean;
  wrap?: boolean;
  full?: boolean;
  gap?: keyof typeof gaps;
}

export interface FlexTypeMap<AdditionalProps = NonNullable<unknown>, RootComponent extends React.ElementType = 'div'> {
  props: AdditionalProps & FlexOwnProps;
  defaultComponent: RootComponent;
}

export type FlexProps<
  RootComponent extends React.ElementType = FlexTypeMap['defaultComponent'],
  AdditionalProps = NonNullable<unknown>,
> = OverrideProps<FlexTypeMap<AdditionalProps, RootComponent>, RootComponent> & { as?: React.ElementType };

export const Flex: OverridableComponent<FlexTypeMap> = ({
  as: Component = 'div',
  style: styleProp,
  className,
  column,
  columnReverse,
  rowReverse,
  justifyStart,
  justifyCenter,
  justifyEnd,
  justifyBetween,
  justifyEvenly,
  alignStart,
  alignCenter,
  alignEnd,
  alignBaseline,
  grow,
  noShrink,
  full,
  wrap,
  gap,
  ...props
}: FlexProps) => {
  let style = styleProp;
  if (gap !== undefined) {
    style = {
      gap: gaps[gap],
      ...styleProp,
    };
  }

  return (
    <Component
      className={cn(classes.Flex, className, {
        [classes.column]: column,
        [classes.columnReverse]: columnReverse,
        [classes.rowReverse]: rowReverse,
        [classes.justifyStart]: justifyStart,
        [classes.justifyCenter]: justifyCenter,
        [classes.justifyEnd]: justifyEnd,
        [classes.justifyBetween]: justifyBetween,
        [classes.justifyEvenly]: justifyEvenly,
        [classes.alignStart]: alignStart,
        [classes.alignCenter]: alignCenter,
        [classes.alignEnd]: alignEnd,
        [classes.alignBaseline]: alignBaseline,
        [classes.grow]: grow,
        [classes.noShrink]: noShrink,
        [classes.full]: full,
        [classes.wrap]: wrap,
      })}
      style={style}
      {...props}
    />
  );
};
